import React, { useState } from 'react';
import { post } from '../../utils/api';

export interface SurveyOption {
  id: string;
  title: string;
  description: string;
}

interface MemberSurveyProps {
  surveyId: string;
  question: string;
  options: SurveyOption[];
  onSubmitted?: () => void;
}

export const MemberSurvey: React.FC<MemberSurveyProps> = ({
  surveyId,
  question,
  options,
  onSubmitted,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [otherChecked, setOtherChecked] = useState(false);
  const [otherText, setOtherText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleOption = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hasSelection = selected.size > 0 || otherChecked;

  const handleSubmit = async () => {
    if (!hasSelection) return;
    setSubmitting(true);
    try {
      const choices = Array.from(selected);
      if (otherChecked) choices.push('other');
      await post('/api/survey/member', {
        surveyId,
        choice: choices.join(','),
        textResponse: otherChecked && otherText.trim() ? otherText.trim() : undefined,
      });
      setSubmitted(true);
      onSubmitted?.();
      window.dispatchEvent(new CustomEvent('member-survey-completed'));
    } catch {
      // Silently fail
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-4">
        <p className="text-sm font-semibold text-foreground">Thanks for your feedback!</p>
        <p className="text-xs text-muted-foreground mt-1">Your response helps us prioritize what to build next.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="font-serif text-lg font-bold text-foreground">{question}</h3>
      <div className="space-y-2">
        {options.map(opt => (
          <label
            key={opt.id}
            className="flex items-center gap-2.5 cursor-pointer rounded-lg px-2.5 py-2 transition-colors hover:bg-muted/50"
          >
            <input
              type="checkbox"
              checked={selected.has(opt.id)}
              onChange={() => toggleOption(opt.id)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary focus:ring-2 cursor-pointer flex-shrink-0"
            />
            <div className="min-w-0">
              <span className="text-sm font-semibold text-foreground">{opt.title}</span>
              <p className="text-xs text-muted-foreground leading-relaxed">{opt.description}</p>
            </div>
          </label>
        ))}
        {/* Other option */}
        <label className="flex items-center gap-2.5 cursor-pointer rounded-lg px-2.5 py-2 transition-colors hover:bg-muted/50">
          <input
            type="checkbox"
            checked={otherChecked}
            onChange={(e) => setOtherChecked(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary focus:ring-2 cursor-pointer flex-shrink-0"
          />
          <div className="min-w-0 flex-1">
            <span className="text-sm font-semibold text-foreground">Other</span>
            {otherChecked && (
              <input
                type="text"
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                placeholder="Tell us what you'd like..."
                className="mt-1.5 w-full rounded-md border border-border bg-white px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                maxLength={200}
              />
            )}
          </div>
        </label>
      </div>
      <button
        onClick={handleSubmit}
        disabled={!hasSelection || submitting}
        className="w-full py-2 px-4 rounded-lg font-semibold text-sm text-white transition-colors duration-200 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? 'Submitting...' : 'Submit'}
      </button>
    </div>
  );
};

export default MemberSurvey;
